import React, { useEffect, useMemo, useState } from "react";
import { ProposalShare } from "@/components/governance/ProposalShare";
import { WatchButton } from "@/components/governance/WatchButton";
import { useGov } from "@/hooks/governance";
import {
  govActionLabel, govStatusTone, adaFromLovelace, asPct, asPct2,
} from "@/lib/governance";
import { unixToDate } from "@/lib/format";
import { Badge, HashCell } from "@/components/primitives";
import { Table } from "@/components/Table";
import {
  GovPageShell, GovCard, GovRow, GovSection, GovError, GovSkeleton, GovEmpty,
  GovTabs, GovPager, GovTallyBar, GovKV,
} from "@/components/governance/GovShell";
import { AnchorLink, proposalVoteColumns } from "@/components/governance/GovParts";

const VOTE_PAGE = 25;
const EPOCH_FALLBACK_SECONDS = 432000;

const CountTile = ({ value, label }) => (
  <div className="flex flex-col items-center min-w-[48px]">
    <span data-testid={`voting-countdown-${label}`} className="text-[22px] sm:text-[26px] font-heading font-semibold mono text-foreground tabular-nums leading-none">{String(value).padStart(2, "0")}</span>
    <span className="mt-1 text-[9px] uppercase tracking-[0.2em] text-muted-foreground mono">{label}</span>
  </div>
);

// --- Live voting watch ---
// Countdown to voting closure for an OPEN (active) action. Closure = end of the
// proposal's expires_after_epoch, derived from live epoch boundaries: the exact
// epoch length comes from consecutive epoch start_times (the upstream `end_time`
// of an in-progress epoch is not the scheduled boundary), so nothing is guessed.
const VotingWatch = ({ d, txHash, certIndex }) => {
  const isOpen = d?.status === "active";
  const latest = useGov("/epochs/latest", { enabled: isOpen });
  const cur = latest.data;
  const prev = useGov(`/epochs/${(cur?.epoch ?? 1) - 1}`, { enabled: isOpen && cur?.epoch != null });
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!isOpen) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [isOpen]);

  const closeTs = useMemo(() => {
    const expires = d?.expires_after_epoch;
    if (expires == null || !cur?.start_time || cur?.epoch == null) return null;
    let len = EPOCH_FALLBACK_SECONDS;
    if (prev.data?.start_time && cur.start_time > prev.data.start_time) len = cur.start_time - prev.data.start_time;
    return (cur.start_time + (expires + 1 - cur.epoch) * len) * 1000;
  }, [d, cur, prev.data]);

  if (!isOpen) return null;
  if (latest.loading && !cur) return <div data-testid="voting-watch" className="h-[76px] rounded-2xl border border-primary/20 bg-primary/[0.03] animate-pulse" />;
  if (!closeTs) return null;

  const remaining = closeTs - now;
  const ended = remaining <= 0;
  const abs = Math.max(0, remaining);
  const days = Math.floor(abs / 86400000);
  const hours = Math.floor((abs % 86400000) / 3600000);
  const mins = Math.floor((abs % 3600000) / 60000);
  const secs = Math.floor((abs % 60000) / 1000);
  const watchTitle = (d?.metadata?.title || "").trim() || govActionLabel(d?.type);

  return (
    <div data-testid="voting-watch" className="relative overflow-hidden rounded-2xl border border-[#22d3ee]/25 bg-gradient-to-r from-[#22d3ee]/[0.08] to-transparent backdrop-blur-md box-glow-accent px-4 sm:px-5 py-4">
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#22d3ee] to-transparent opacity-70 tracing-beam pointer-events-none" />
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#22d3ee] opacity-60 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#22d3ee]" />
            </span>
            <span className="text-[10px] uppercase tracking-[0.25em] mono text-[#22d3ee]">Live voting watch</span>
          </div>
          <div data-testid="voting-watch-headline" className="text-[13px] text-foreground">
            {ended ? "Voting period has ended — awaiting ratification" : "Voting closes in"}
          </div>
          <div className="text-[11px] mono text-muted-foreground">
            End of epoch {d.expires_after_epoch} · {unixToDate(Math.floor(closeTs / 1000))}
          </div>
          {!ended ? (
            <div className="pt-1.5">
              <WatchButton
                item={{ txHash, certIndex, title: watchTitle, type: d.type, status: d.status, closeTs }}
                tid="voting-watch-toggle"
                compact
              />
            </div>
          ) : null}
        </div>
        {ended ? (
          <Badge tone="default">voting closed</Badge>
        ) : (
          <div data-testid="voting-countdown" className="flex items-center gap-2 sm:gap-3">
            <CountTile value={days} label="days" />
            <span className="text-muted-foreground/40 text-lg">:</span>
            <CountTile value={hours} label="hrs" />
            <span className="text-muted-foreground/40 text-lg">:</span>
            <CountTile value={mins} label="min" />
            <span className="text-muted-foreground/40 text-lg">:</span>
            <CountTile value={secs} label="sec" />
          </div>
        )}
      </div>
    </div>
  );
};

// --- Overview tab ---
const Overview = ({ d, txHash, certIndex }) => {
  const vc = d.vote_counts || {};
  return (
    <div className="space-y-4">
      <VotingWatch d={d} txHash={txHash} certIndex={certIndex} />
      <div className="grid lg:grid-cols-2 gap-4">
      <GovCard>
        <GovRow label="Action ID"><span className="inline-flex items-center gap-2"><HashCell value={d.gov_action_id} len={14} /></span></GovRow>
        <GovRow label="Type">{govActionLabel(d.type)}</GovRow>
        <GovRow label="Status"><Badge tone={govStatusTone(d.status)}>{d.status}</Badge></GovRow>
        <GovRow label="Deposit">{adaFromLovelace(d.deposit)}</GovRow>
        <GovRow label="Return Address"><HashCell value={d.return_address} len={12} /></GovRow>
        <GovRow label="Submitted">epoch {d.submitted_epoch ?? "—"}</GovRow>
        <GovRow label="Expires">epoch {d.expires_after_epoch ?? "—"}</GovRow>
        <GovRow label="Block"><HashCell value={d.tx_hash} len={10} /> · {d.block_time ? unixToDate(d.block_time) : "—"}</GovRow>
        {d.ratified_epoch ? <GovRow label="Ratified">epoch {d.ratified_epoch}</GovRow> : null}
        {d.enacted_epoch ? <GovRow label="Enacted">epoch {d.enacted_epoch}</GovRow> : null}
        {d.expired_epoch ? <GovRow label="Expired">epoch {d.expired_epoch}</GovRow> : null}
        {d.dropped_epoch ? <GovRow label="Dropped">epoch {d.dropped_epoch}</GovRow> : null}
        <GovRow label="Rationale"><AnchorLink url={d.anchor_url} /></GovRow>
      </GovCard>

      <div className="space-y-4">
        {(d.metadata?.title || d.metadata?.abstract || d.metadata?.rationale) ? (
          <GovCard>
            {d.metadata.title ? <div className="text-[15px] font-medium text-foreground mb-2">{d.metadata.title}</div> : null}
            {d.metadata.abstract ? <p className="text-[13px] text-muted-foreground leading-relaxed">{d.metadata.abstract}</p> : null}
            {d.metadata.rationale ? <p className="text-[12px] text-muted-foreground/80 leading-relaxed mt-2">{d.metadata.rationale}</p> : null}
          </GovCard>
        ) : (
          <GovCard>
            <div className="text-[12px] text-muted-foreground mono">
              Off-chain metadata {d.metadata?.is_valid ? "available" : "not fetched / unavailable"}. See the rationale anchor for full context.
            </div>
          </GovCard>
        )}
        <GovCard>
          <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mono mb-3">Vote Counts</div>
          <div className="space-y-3">
            {[["DReps", vc.drep], ["Stake Pools", vc.spo], ["Committee", vc.constitutional_committee]].map(([label, g]) => (
              <div key={label} className="space-y-1.5">
                <div className="flex items-center justify-between text-[11px] mono">
                  <span className="text-foreground">{label}</span>
                  <span className="text-muted-foreground">{(g?.yes ?? 0)} / {(g?.no ?? 0)} / {(g?.abstain ?? 0)}</span>
                </div>
                <GovTallyBar yes={g?.yes} no={g?.no} abstain={g?.abstain} testid={`vc-bar-${label}`} />
              </div>
            ))}
          </div>
        </GovCard>
      </div>
      </div>
    </div>
  );
};

// --- Votes tab ---
const Votes = ({ txHash, certIndex }) => {
  const [page, setPage] = useState(1);
  const { data, error, loading, refetch } = useGov(
    `/governance/proposals/${txHash}/${certIndex}/votes`,
    { params: { count: VOTE_PAGE, page } },
  );
  const rows = Array.isArray(data) ? data : [];
  if (error) return <GovError error={error} onRetry={refetch} />;
  return (
    <>
      <Table columns={proposalVoteColumns()} rows={rows} loading={loading} skeletonRows={8} empty="No votes recorded yet." />
      <GovPager page={page} onPage={setPage} hasNext={rows.length === VOTE_PAGE} loading={loading} />
    </>
  );
};

const GroupTally = ({ title, g, countKey }) => {
  if (!g) return null;
  if (g.data_status && g.data_status !== "available") {
    return <GovCard><div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mono mb-2">{title}</div><GovEmpty>{g.unavailable_reason || "Unavailable"}</GovEmpty></GovCard>;
  }
  const counts = countKey ? (g[countKey] || {}) : { yes: g.yes, no: g.no, abstain: g.abstain };
  return (
    <GovCard>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mono">{title}</div>
        {g.required === false ? <Badge>not required</Badge> : g.meets_threshold === true ? <Badge tone="success">meets threshold</Badge> : g.meets_threshold === false ? <Badge tone="danger">below threshold</Badge> : null}
      </div>
      <GovTallyBar yes={g.yes} no={g.no_total ?? g.no} abstain={g.abstain} />
      <div className="mt-3 space-y-2">
        <GovKV label="Approval ratio">{asPct2(g.ratio)}</GovKV>
        <GovKV label="Threshold">{g.threshold != null ? asPct(g.threshold) : "—"}</GovKV>
        {counts ? (
          <GovKV label="Voters (Y/N/A)">
            {(counts.yes ?? 0)} / {(counts.no ?? 0)} / {(counts.abstain ?? 0)}
          </GovKV>
        ) : null}
      </div>
    </GovCard>
  );
};

// --- Tally tab ---
const Tally = ({ txHash, certIndex }) => {
  const { data: t, error, loading, refetch } = useGov(`/governance/proposals/${txHash}/${certIndex}/tally`);
  const hist = useGov(`/governance/proposals/${txHash}/${certIndex}/tally/history`);
  if (error) return <GovError error={error} onRetry={refetch} />;
  if (loading && !t) return <GovSkeleton lines={6} />;
  if (!t) return <GovEmpty />;
  const checks = Array.isArray(hist.data?.checks) ? hist.data.checks : [];
  return (
    <div className="space-y-4">
      {t.disclaimer ? (
        <div className="border border-amber-500/30 bg-amber-500/5 text-amber-300/90 rounded-lg px-4 py-2.5 text-[11px] mono">
          {t.estimate ? "Estimate · " : ""}{t.disclaimer}
        </div>
      ) : null}
      <div className="grid lg:grid-cols-3 gap-4">
        <GroupTally title="DReps" g={t.drep} countKey="dreps" />
        <GroupTally title="Stake Pools" g={t.spo} countKey="pools" />
        <GroupTally title="Committee" g={t.committee} countKey={null} />
      </div>
      {checks.length ? (
        <GovSection title="Approval ratio by check epoch">
          <Table
            columns={[
              { key: "epoch", label: "Check Epoch", render: (r) => <span className="text-foreground">{r.check_epoch}</span> },
              { key: "kind", label: "Kind", render: (r) => <span className="text-muted-foreground">{r.kind}</span> },
              { key: "drep", label: "DRep", align: "right", render: (r) => asPct2(r.drep?.ratio) },
              { key: "spo", label: "SPO", align: "right", render: (r) => asPct2(r.spo?.ratio) },
              { key: "cc", label: "Committee", align: "right", render: (r) => asPct2(r.committee?.ratio) },
            ]}
            rows={checks}
            loading={hist.loading}
            empty="No historical checks."
          />
        </GovSection>
      ) : null}
    </div>
  );
};

// --- Timeline tab ---
const Timeline = ({ txHash, certIndex }) => {
  const { data, error, loading, refetch } = useGov(`/governance/proposals/${txHash}/${certIndex}/timeline`);
  if (error) return <GovError error={error} onRetry={refetch} />;
  if (loading && !data) return <GovSkeleton lines={5} />;
  const events = Array.isArray(data?.events) ? data.events : [];
  if (!events.length) return <GovEmpty>No lifecycle events recorded.</GovEmpty>;
  return (
    <div className="relative pl-5 space-y-4" data-testid="gov-timeline">
      <div className="absolute left-1.5 top-1 bottom-1 w-px bg-primary/20" />
      {events.map((e, i) => (
        <div key={i} className="relative">
          <span className="absolute -left-[13px] top-1.5 w-2.5 h-2.5 rounded-full bg-primary border-2 border-background" />
          <div className="flex items-center gap-2 flex-wrap">
            <Badge tone="info">{String(e.event_type).replace(/_/g, " ")}</Badge>
            <span className="text-[11px] mono text-muted-foreground">epoch {e.epoch}</span>
            {e.time ? <span className="text-[11px] mono text-muted-foreground/70">{unixToDate(e.time)}</span> : null}
          </div>
          {e.details ? (
            <div className="mt-1.5 text-[11px] mono text-muted-foreground/80 space-y-0.5">
              {Object.entries(e.details).map(([k, v]) => (
                <div key={k}>{k.replace(/_/g, " ")}: <span className="text-foreground/80">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span></div>
              ))}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
};

// --- Ratification tab ---
const Ratification = ({ txHash, certIndex }) => {
  const { data: r, error, loading, refetch } = useGov(`/governance/proposals/${txHash}/${certIndex}/ratification`);
  if (error) return <GovError error={error} onRetry={refetch} />;
  if (loading && !r) return <GovSkeleton lines={5} />;
  if (!r) return <GovEmpty />;
  const a = r.assessment || {};
  const groups = r.groups || {};
  return (
    <div className="space-y-4">
      <GovCard>
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mono">Assessment</div>
          <Badge tone={a.status === "ratifiable" ? "success" : a.status === "not_ratifiable" ? "default" : "info"}>{a.status || "—"}</Badge>
        </div>
        {a.explanation ? <p className="text-[13px] text-muted-foreground leading-relaxed">{a.explanation}</p> : null}
        {r.check_epoch ? <div className="text-[11px] mono text-muted-foreground/70 mt-2">check epoch {r.check_epoch} · {r.kind}</div> : null}
      </GovCard>
      <div className="grid lg:grid-cols-3 gap-4">
        {["drep", "spo", "committee"].map((k) => {
          const g = groups[k];
          if (!g) return null;
          return (
            <GovCard key={k}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mono">{k === "spo" ? "Stake Pools" : k === "drep" ? "DReps" : "Committee"}</div>
                <Badge tone={g.result === "pass" || g.result === "met" ? "success" : g.result === "not_required" ? "default" : g.result === "fail" ? "danger" : "info"}>{g.result || "—"}</Badge>
              </div>
              <GovKV label="Ratio">{asPct2(g.ratio)}</GovKV>
              <div className="mt-2"><GovKV label="Threshold">{g.threshold != null ? asPct(g.threshold) : "—"}</GovKV></div>
              {g.data_status && g.data_status !== "available" ? <div className="text-[10px] mono text-amber-300/80 mt-2">{g.unavailable_reason || g.data_status}</div> : null}
            </GovCard>
          );
        })}
      </div>
    </div>
  );
};

// --- Parameter changes tab ---
const paramVal = (c, key) => {
  const v = c?.[key];
  if (v === null || v === undefined) return "—";
  return c.value_type === "lovelace" ? adaFromLovelace(v) : String(v);
};

const outcomeTone = (o) => (o === "enacted" ? "success" : o === "not_enacted" ? "default" : "info");

const ParamChanges = ({ txHash, certIndex }) => {
  const { data, error, loading, refetch } = useGov(`/governance/proposals/${txHash}/${certIndex}/parameter_changes`);
  if (error) return <GovError error={error} onRetry={refetch} />;
  if (loading && !data) return <GovSkeleton lines={5} />;
  if (!data) return <GovEmpty />;
  const params = Array.isArray(data.parameters) ? data.parameters : [];
  return (
    <div className="space-y-4">
      <GovCard>
        <GovRow label="Lifecycle"><Badge tone={govStatusTone(data.lifecycle?.status)}>{data.lifecycle?.status || "—"}</Badge></GovRow>
        <GovRow label="Enacted">{data.enactment?.enacted ? `epoch ${data.enactment.epoch}` : "no"}</GovRow>
        {data.guardrail_script_hash ? <GovRow label="Guardrail Script"><HashCell value={data.guardrail_script_hash} len={12} /></GovRow> : null}
      </GovCard>
      <GovSection title={`Proposed parameter changes (${params.length})`}>
        {params.length ? (
          <Table
            columns={[
              { key: "parameter", label: "Parameter", render: (r) => (
                <span className="inline-flex items-center gap-2 flex-wrap">
                  <span className="text-foreground">{r.parameter}</span>
                  <Badge>{r.group || "—"}</Badge>
                  {r.security_group ? <Badge tone="warn">security</Badge> : null}
                </span>
              ) },
              { key: "proposed", label: "Proposed", align: "right", render: (r) => <span className="text-primary">{paramVal(r.components?.[0], "proposed")}</span> },
              { key: "current", label: "At Submission", align: "right", render: (r) => <span className="text-muted-foreground">{paramVal(r.components?.[0], "at_submission")}</span> },
              { key: "outcome", label: "Outcome", align: "right", render: (r) => <Badge tone={outcomeTone(r.outcome)}>{r.outcome || "—"}</Badge> },
            ]}
            rows={params}
            empty="No parameter detail."
          />
        ) : <GovEmpty />}
      </GovSection>
    </div>
  );
};

// --- Withdrawals tab ---
const Withdrawals = ({ txHash, certIndex }) => {
  const { data, error, loading, refetch } = useGov(`/governance/proposals/${txHash}/${certIndex}/withdrawals`);
  if (error) return <GovError error={error} onRetry={refetch} />;
  if (loading && !data) return <GovSkeleton lines={5} />;
  if (!data) return <GovEmpty />;
  const recipients = data.proposed?.recipients || [];
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-3 gap-3">
        <GovCard><div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono">Requested total</div><div className="text-[18px] mono mt-1">{adaFromLovelace(data.proposed?.requested_total)}</div></GovCard>
        <GovCard><div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono">Recipients</div><div className="text-[18px] mono mt-1">{data.proposed?.recipient_count ?? recipients.length}</div></GovCard>
        <GovCard><div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono">Status</div><div className="mt-2"><Badge tone={govStatusTone(data.lifecycle?.status)}>{data.lifecycle?.status || "—"}</Badge></div></GovCard>
      </div>
      <Table
        columns={[
          { key: "addr", label: "Recipient (stake addr)", render: (r) => <HashCell value={r.stake_address} len={12} /> },
          { key: "cred", label: "Credential", render: (r) => <Badge>{r.credential_type || "—"}</Badge> },
          { key: "amount", label: "Amount", align: "right", render: (r) => <span className="text-foreground">{adaFromLovelace(r.amount)}</span> },
        ]}
        rows={recipients}
        empty="No recipients."
      />
    </div>
  );
};

export const ProposalDetail = ({ meta, txHash, certIndex }) => {
  const { data: d, error, loading, refetch } = useGov(`/governance/proposals/${txHash}/${certIndex}`);
  const [tab, setTab] = useState("overview");

  const tabs = useMemo(() => {
    const base = [
      { key: "overview", label: "Overview" },
      { key: "votes", label: "Votes" },
      { key: "tally", label: "Tally" },
      { key: "timeline", label: "Timeline" },
      { key: "ratification", label: "Ratification" },
    ];
    if (d?.type === "parameter_change") base.push({ key: "params", label: "Parameter Changes" });
    if (d?.type === "treasury_withdrawals") base.push({ key: "withdrawals", label: "Withdrawals" });
    return base;
  }, [d]);

  const subtitle = d ? `${govActionLabel(d.type)} · ${d.status}` : "Governance action";

  return (
    <GovPageShell crumbs={meta.crumbs} title={d ? govActionLabel(d.type) : meta.title} subtitle={subtitle} actions={d ? <ProposalShare txHash={txHash} certIndex={certIndex} d={d} /> : null}>
      {error ? (
        <GovError error={error} onRetry={refetch} />
      ) : loading && !d ? (
        <GovSkeleton lines={8} />
      ) : d ? (
        <>
          <GovTabs tabs={tabs} active={tab} onChange={setTab} />
          <div data-testid={`gov-proposal-tab-${tab}`}>
            {tab === "overview" && <Overview d={d} txHash={txHash} certIndex={certIndex} />}
            {tab === "votes" && <Votes txHash={txHash} certIndex={certIndex} />}
            {tab === "tally" && <Tally txHash={txHash} certIndex={certIndex} />}
            {tab === "timeline" && <Timeline txHash={txHash} certIndex={certIndex} />}
            {tab === "ratification" && <Ratification txHash={txHash} certIndex={certIndex} />}
            {tab === "params" && <ParamChanges txHash={txHash} certIndex={certIndex} />}
            {tab === "withdrawals" && <Withdrawals txHash={txHash} certIndex={certIndex} />}
          </div>
        </>
      ) : <GovEmpty />}
    </GovPageShell>
  );
};
